import { useMemo } from 'react';

import { Client, useClient } from 'urql';
import { fromPromise } from 'xstate5';

import { GenerateTransactionPlanInput } from '@sg/graphql-schema';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import {
  type FetchPaymentBreakdownParams,
  type FetchPaymentBreakdownResponse,
} from '../../machine';
import {
  PaymentBreakdownDocument,
  type PaymentBreakdownQuery,
} from './graphql/PaymentBreakdown.generated';

export const useFetchPaymentBreakdown = () => {
  const isFlexEnabled = useFeatureFlag('CELS-5522-flex-enabled');
  const client = useClient();
  const { track } = useTelemetry();

  const fetchPaymentBreakdown = useMemo(
    () =>
      fromPromise<FetchPaymentBreakdownResponse, FetchPaymentBreakdownParams>(async (params) => {
        const { input } = params;

        if (!isFlexEnabled || !input.useHsa) return { amountEligibleForFlex: 0 };

        const amountEligibleForFlex = await fetchAmountEligibleForFlex(client, input);
        const splitType = getSplitType(amountEligibleForFlex);

        track('bag.payment_split.calculated', { splitType });

        return { amountEligibleForFlex };
      }),
    [isFlexEnabled, track, client],
  );

  return { fetchPaymentBreakdown };
};

/**
 * Given a transaction plan, find the step related to flex and extract the amount to be used.
 */
async function fetchAmountEligibleForFlex(client: Client, input: GenerateTransactionPlanInput) {
  const query = client.query<PaymentBreakdownQuery>;
  const response = await query(PaymentBreakdownDocument, { input }, OPTIONS).toPromise();

  const result = response.data?.cart?.transactionPlan;
  const hasTransactionPlan = result?.__typename === 'TransactionPlanResult';

  if (!hasTransactionPlan) return null;

  const flexStep = result.transactionPlan.find((plan) => plan.processor === FLEX_PROCESSOR);

  return flexStep?.amountInCents ?? null;
}

function getSplitType(amountEligibleForFlex: number | null) {
  if (amountEligibleForFlex === null) return 'failed_to_calculate';

  return amountEligibleForFlex > 0 ? 'partial_eligibility' : 'no_split';
}

// ─── Constants ───────────────────────────────────────────────────────────────

const FLEX_PROCESSOR = 'flex';
const OPTIONS = { requestPolicy: 'network-only' as const };
